"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateBillingDetailsRequestSchema = exports.billingDetailsSchema = exports.BILLING_COUNTRIES = void 0;
exports.vatLooksValid = vatLooksValid;
exports.billingEmailLooksValid = billingEmailLooksValid;
const zod_1 = require("zod");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Billing details — C16, under decision R-5.                                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `markup.html` 1587–1710. Read the header of `0023_billing_details.sql` first: it splits the
 * mockup's six billing panels by **who owns the data**, and only one of them is the customer's.
 */
/** The mockup's `wsCountryOptions`. */
exports.BILLING_COUNTRIES = ['HU', 'DE', 'AT', 'RO', 'GB', 'US'];
/**
 * Is this a plausible VAT number for this country?
 *
 * ⚠️⚠️ **THE MOCKUP'S OWN RULE, AND IT IS COUNTRY-DEPENDENT** — `HU` wants `HU` plus eight digits,
 * anything else a country code plus eight to twelve characters. Here rather than in a CHECK because a
 * cross-column predicate that changes with the country would have to hard-code every country's format
 * in the schema, and would then refuse a legitimate number the day a rule changes.
 *
 * ⚠️ **An empty string is VALID.** A workspace that has not filled the form in yet is not in error,
 * and a validation that fired on an untouched form is a validation people learn to ignore. The mockup
 * disagrees — its Save is disabled until the field is filled — and that is a prototype with one
 * seeded customer.
 */
function vatLooksValid(country, vat) {
    if (vat === '')
        return true;
    return country === 'HU' ? /^HU\d{8}$/.test(vat) : /^[A-Z]{2}[A-Z0-9]{8,12}$/.test(vat);
}
/** The same shape the mockup's `billEmailOk` checks, and an empty string is valid for the same reason. */
function billingEmailLooksValid(email) {
    return email === '' || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
}
exports.billingDetailsSchema = zod_1.z.object({
    company: zod_1.z.string(),
    email: zod_1.z.string(),
    address: zod_1.z.string(),
    countryCode: zod_1.z.string(),
    vatNumber: zod_1.z.string(),
    /**
     * How many active members hold a seat right now.
     *
     * ⚠️⚠️ **USED, NOT ENTITLED.** The mockup shows `seatsUsed / seatsTotal` with a percentage bar; the
     * total is a number a payment provider sells and nothing here can know. So this is the count we
     * genuinely have, and the screen does not draw a bar against an invented denominator.
     *
     * ⚠️ Deactivated members do not hold one, which is the mockup's own note: *"A deaktivált tagok nem
     * foglalnak helyet."*
     */
    seatsUsed: zod_1.z.number().int().nonnegative(),
    /* ── S6, and the half of the mockup's billing view C16 could not build ─────────────────── */
    /**
     * The plan an operator assigned, or `null` during a trial.
     *
     * ⚠️⚠️ **THE PANEL C16 REFUSED IS NOW BUILDABLE, AND THE REASON IT REFUSED STILL HOLDS FOR THE
     * REST.** Its objection was to *fabrication*: *"storing `plan: 'trial'`, a card ending 4242 and
     * three invoice rows would be a fabricated billing history — the one kind of placeholder that
     * reads as fact."* A plan a human typed into the Control Panel is not fabricated, and neither is
     * the seat count. **The card, the invoice list and the renewal date remain absent**, because those
     * are a payment provider's and there is no provider — R-5 is untouched.
     *
     * ⚠️ `seatPrice` is a string: `numeric(14,2)` does not survive a JavaScript number.
     */
    plan: zod_1.z
        .object({
        name: zod_1.z.string(),
        seatPrice: zod_1.z.string(),
        currency: zod_1.z.string(),
    })
        .nullable(),
    /**
     * The tenant's seats, which are a different question from `seatsUsed` above.
     *
     * ⚠️⚠️ **`seatsUsed` IS THIS WORKSPACE'S AND THIS IS THE TENANT'S, AND NEITHER WAS REDEFINED TO
     * MEAN THE OTHER.** The mockup's number is the workspace's, C16's test asserts it, and the
     * allowance is the tenant's because the plan is. They coincide today only because 0031 refuses a
     * second workspace; quietly widening the existing field would have made the screen start answering
     * a different question with no way to notice.
     */
    tenantSeats: zod_1.z.object({
        used: zod_1.z.number().int().nonnegative(),
        /** ⚠️ Shown separately: an unaccepted invitation holds a seat, and that has to be visible. */
        pending: zod_1.z.number().int().nonnegative(),
        /** `null` is unlimited — the ordinary state of a tenant with no plan. */
        limit: zod_1.z.number().int().nullable(),
    }),
    /**
     * When the trial ends, for the banner the mockup draws.
     *
     * ⚠️ A real date from `tenants.trial_ends_at` (0033), not a countdown this screen invents — and
     * `null` for a tenant that is not on a trial, where the banner is simply not drawn.
     */
    trialEndsAt: zod_1.z.string().nullable(),
    version: zod_1.z.number().int(),
});
exports.updateBillingDetailsRequestSchema = zod_1.z.object({
    company: zod_1.z.string().trim().max(200),
    email: zod_1.z.string().trim().max(320),
    address: zod_1.z.string().trim().max(300),
    countryCode: zod_1.z.enum(exports.BILLING_COUNTRIES),
    vatNumber: zod_1.z.string().trim().toUpperCase().max(20),
    version: zod_1.z.number().int().nonnegative(),
});
//# sourceMappingURL=billing.js.map